import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { DashboardService }     from '../dashboard/services/dashboard.service';
import { JobService }           from '../jobs/services/job.service';
import { UserService }          from '../users/services/user.service';
import { PdfService }           from '../../core/services/pdf.service';
import { ExcelService }         from '../../core/services/excel.service';
import { SubscriptionService }  from '../../core/services/subscription.service';

import { TaskLog } from '../jobs/models/task-log.model';
import { Job }     from '../jobs/models/job.model';
import { Worker }  from '../users/models/worker.model';

import Swal from 'sweetalert2';

@Component({
  selector:    'app-reports',
  templateUrl: './reports.component.html',
  styleUrls:   ['./reports.component.css']
})
export class ReportsComponent implements OnInit {

  allLogs:      TaskLog[] = [];
  jobs:         Job[]     = [];
  workers:      Worker[]  = [];
  filteredLogs: TaskLog[] = [];
  pagedLogs:    TaskLog[] = [];

  loading   = true;
  exporting = false;
  exportingPdf         = false;  

  // ✅ Plan state
  currentPlan  = '';
  canExport    = false;   // Starter and above
  loadingPlan  = true;

  filters = {
    dateFrom: '',
    dateTo:   '',
    workerId: '',
    jobId:    ''
  };

  searchQuery = '';
  activeRange: 'today' | 'week' | 'month' | 'quarter' | '' = '';

  sortField: 'worker' | 'job' | 'hours' | 'date' = 'date';
  sortDir:   'asc' | 'desc' = 'desc';

  currentPage = 1;
  pageSize    = 15;
  totalPages  = 1;
  pageNumbers: number[] = [];

  Math = Math;

  private avatarColors = [
    'linear-gradient(135deg, #1F3A5F, #2BB0A6)',
    'linear-gradient(135deg, #2BB0A6, #1F8F88)',
    'linear-gradient(135deg, #3498DB, #1F3A5F)',
    'linear-gradient(135deg, #F5A623, #E8821A)',
    'linear-gradient(135deg, #2ECC71, #1A9E4C)',
    'linear-gradient(135deg, #9B59B6, #6C3483)',
    'linear-gradient(135deg, #E74C3C, #C0392B)',
    'linear-gradient(135deg, #1ABC9C, #148F77)',
  ];

  constructor(
    private dashboardService:    DashboardService,
    private jobService:          JobService,
    private userService:         UserService,
    private pdfService:          PdfService,
    private excelService:        ExcelService,
    private subscriptionService: SubscriptionService,  // ✅ new
    private router:              Router                // ✅ new
  ) {}

  ngOnInit(): void {
    this.loadAll();
    this.setRange('month');
    this.loadPlanStatus();  // ✅ new
  }

  // ✅ Load plan to determine export access
  private async loadPlanStatus(): Promise<void> {
    try {
      const sub        = await firstValueFrom(this.subscriptionService.loadCurrent());
      this.currentPlan = sub.plan;
      // ✅ Starter and above can export (hasExcelExport = true on Starter+)
      this.canExport   = sub.limits.hasExcelExport;
    } catch {
      this.canExport = false;
    } finally {
      this.loadingPlan = false;
    }
  }

  loadAll(): void {
    this.loading = true;
    this.userService.getWorkers().subscribe(w => (this.workers = w));

    this.jobService.getJobs().subscribe(jobs => {
      this.jobs = jobs;

      const logRequests = jobs.map(j =>
        this.jobService.getJobLogs(j.id).toPromise()
      );

      Promise.all(logRequests).then(results => {
        this.allLogs = results
          .filter((r): r is TaskLog[] => r != null)
          .flat();
        this.loading = false;
        this.applyFilters();
      });
    });
  }

  // ── Filters ───────────────────────────────────────────────

  get hasActiveFilters(): boolean {
    return !!(
      this.filters.dateFrom ||
      this.filters.dateTo   ||
      this.filters.workerId ||
      this.filters.jobId    ||
      this.searchQuery      ||
      this.activeRange
    );
  }

  applyFilters(): void {
    let result = [...this.allLogs];

    if (this.filters.dateFrom) {
      const from = new Date(this.filters.dateFrom);
      from.setHours(0, 0, 0, 0);
      result = result.filter(l => new Date(l.createdUtc) >= from);
    }

    if (this.filters.dateTo) {
      const to = new Date(this.filters.dateTo);
      to.setHours(23, 59, 59, 999);
      result = result.filter(l => new Date(l.createdUtc) <= to);
    }

    if (this.filters.workerId)
      result = result.filter(l => l.userId === this.filters.workerId);

    if (this.filters.jobId)
      result = result.filter(l => l.jobId === this.filters.jobId);

    if (this.searchQuery.trim()) {
      const q = this.searchQuery.toLowerCase();
      result = result.filter(l =>
        this.getWorkerName(l.userId).toLowerCase().includes(q) ||
        this.getJobTitle(l.jobId).toLowerCase().includes(q)    ||
        (l.notes ?? '').toLowerCase().includes(q)
      );
    }

    result            = this.sortLogs(result);
    this.filteredLogs = result;
    this.currentPage  = 1;
    this.buildPagination();
    this.updatePage();
  }

  onSearch(): void {
    this.activeRange = '';
    this.applyFilters();
  }

  resetFilters(): void {
    this.filters     = { dateFrom: '', dateTo: '', workerId: '', jobId: '' };
    this.searchQuery = '';
    this.activeRange = '';
    this.applyFilters();
  }

  setRange(range: 'today' | 'week' | 'month' | 'quarter'): void {
    this.activeRange = range;
    const now   = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    let from: Date;

    switch (range) {
      case 'today':   from = today; break;
      case 'week':
        from = new Date(today);
        from.setDate(today.getDate() - today.getDay());
        break;
      case 'month':
        from = new Date(today.getFullYear(), today.getMonth(), 1);
        break;
      case 'quarter':
        const q = Math.floor(today.getMonth() / 3);
        from = new Date(today.getFullYear(), q * 3, 1);
        break;
    }

    this.filters.dateFrom = this.formatDate(from!);
    this.filters.dateTo   = this.formatDate(now);
    this.applyFilters();
  }

  private formatDate(d: Date): string {
    return d.toISOString().split('T')[0];
  }

  // ── Sorting ───────────────────────────────────────────────

  sortBy(field: 'worker' | 'job' | 'hours' | 'date'): void {
    if (this.sortField === field) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDir   = field === 'date' ? 'desc' : 'asc';
    }
    this.applyFilters();
  }

  private sortLogs(logs: TaskLog[]): TaskLog[] {
    return [...logs].sort((a, b) => {
      let valA: any, valB: any;
      switch (this.sortField) {
        case 'worker': valA = this.getWorkerName(a.userId); valB = this.getWorkerName(b.userId); break;
        case 'job':    valA = this.getJobTitle(a.jobId);    valB = this.getJobTitle(b.jobId);    break;
        case 'hours':  valA = a.hoursSpent;                 valB = b.hoursSpent;                 break;
        default:       valA = new Date(a.createdUtc).getTime(); valB = new Date(b.createdUtc).getTime();
      }
      if (valA < valB) return this.sortDir === 'asc' ? -1 : 1;
      if (valA > valB) return this.sortDir === 'asc' ?  1 : -1;
      return 0;
    });
  }

  // ── Pagination ────────────────────────────────────────────

  buildPagination(): void {
    this.totalPages  = Math.max(1, Math.ceil(this.filteredLogs.length / this.pageSize));
    this.pageNumbers = Array.from({ length: this.totalPages }, (_, i) => i + 1)
      .filter(p => Math.abs(p - this.currentPage) <= 2);
  }

  updatePage(): void {
    const start    = (this.currentPage - 1) * this.pageSize;
    this.pagedLogs = this.filteredLogs.slice(start, start + this.pageSize);
    this.buildPagination();
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages) return;
    this.currentPage = page;
    this.updatePage();
  }

  // ── Summary ───────────────────────────────────────────────

  get totalHours(): number {
    return this.filteredLogs.reduce((s, l) => s + (l.hoursSpent || 0), 0);
  }

  get activeWorkerCount(): number {
    return new Set(this.filteredLogs.map(l => l.userId)).size;
  }

  get activeJobCount(): number {
    return new Set(this.filteredLogs.map(l => l.jobId)).size;
  }

  get avgHoursPerLog(): string {
    if (!this.filteredLogs.length) return '0';
    return (this.totalHours / this.filteredLogs.length).toFixed(1);
  }

  // ── Exports ───────────────────────────────────────────────

  async exportPdf(): Promise<void> {
  if (!this.filteredLogs.length) return;

  if (!this.canExport) {
    await this.showExportUpgradePrompt('PDF', 'Starter');
    return;
  }

  if (this.exportingPdf) return;
  this.exportingPdf = true;

  try {
    // ✅ Pass current filters so backend matches what user sees on screen
    await this.pdfService.generateAllJobsReport({
      dateFrom: this.filters.dateFrom || undefined,
      dateTo:   this.filters.dateTo   || undefined,
      workerId: this.filters.workerId || undefined,
      jobId:    this.filters.jobId    || undefined
    });
  } catch (err) {
    console.error('PDF export failed:', err);
    await Swal.fire({
      title:              'Export Failed',
      text:               'PDF could not be generated. Please try again.',
      icon:               'error',
      confirmButtonColor: '#2BB0A6',
      customClass:        { popup: 'swal-fieldops' }
    });
  } finally {
    this.exportingPdf = false;
  }
}

  async exportExcel(): Promise<void> {
    if (!this.filteredLogs.length) return;

    // ✅ Block if Free plan
    if (!this.canExport) {
      await this.showExportUpgradePrompt('Excel', 'Starter');
      return;
    }

    this.exporting = true;
    try {
      await this.excelService.exportJobLogs(
        'report',
        this.filteredLogs,
        this.getWorkerName.bind(this)
      );
    } finally {
      this.exporting = false;
    }
  }

  // ✅ Reusable upgrade prompt for export buttons
  private async showExportUpgradePrompt(
    format: string,
    requiredPlan: string
  ): Promise<void> {
    const result = await Swal.fire({
      title: `${format} Export — Upgrade Required`,
      html: `
        <div style="text-align:left; font-size:14px; line-height:1.8; color:#374151;">
          <p>
            Your current plan is <strong>${this.currentPlan}</strong>.
            ${format} export requires <strong>${requiredPlan}</strong> or higher.
          </p>
          <p style="margin-top:8px; color:#6B7280; font-size:13px;">
            Upgrade now to unlock exports, remove PDF watermarks,
            and access full analytics.
          </p>
        </div>`,
      icon:              'warning',
      confirmButtonText: 'View Pricing',
      showCancelButton:  true,
      cancelButtonText:  'Not Now',
      confirmButtonColor: '#2BB0A6',
      cancelButtonColor:  '#6B7280',
      customClass: { popup: 'swal-fieldops' }
    });

    if (result.isConfirmed)
      this.router.navigate(['/pricing']);
  }

  // ── Helpers ───────────────────────────────────────────────

  getWorkerName(userId?: string | null): string {
    if (!userId) return 'Unknown';
    return this.workers.find(w => w.id === userId)?.fullName ?? 'Unknown';
  }

  getJobTitle(jobId?: string | null): string {
    if (!jobId) return 'Unknown Job';
    return this.jobs.find(j => j.id === jobId)?.title ?? 'Unknown Job';
  }

  getInitials(name: string): string {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    return parts.length >= 2
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : name.slice(0, 2).toUpperCase();
  }

  getAvatarColor(userId: string): string {
    const code = userId?.charCodeAt(0) ?? 0;
    return this.avatarColors[code % this.avatarColors.length];
  }
}